import * as Dialog from '@radix-ui/react-dialog'
import { useState, useEffect, useRef, useCallback } from 'react'
import { X, Loader2, Settings, SlidersHorizontal, Activity } from 'lucide-react'
import { CameraHttpSettingsSheet } from '@/components/cameras/CameraHttpSettingsSheet'
import { CameraG100SettingsSheet } from '@/components/cameras/CameraG100SettingsSheet'   // #403
import { CameraG100DeviceSheet } from '@/components/cameras/CameraG100DeviceSheet'   // #411
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { camerasApi, cameraGroupsApi } from '@/api/endpoints'
import { useAuthStore } from '@/store/authStore'
import { canChangeCameraSettings, hasFullRights } from '@/lib/sharePermissions'
import { useDirectionPreview } from '@/store/directionPreview'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { planGroupSaveOps } from '@/lib/cameraGroupOps'
import type { Camera, CameraGroup, DefaultEventCategory } from '@/types'
import { LookupAddressButton } from '@/components/maps/LookupAddressButton'
import { SwitchRow } from '@/components/cameras/detection/SwitchRow'
import { OnlineStatusModeRow } from '@/components/cameras/OnlineStatusModeRow'
import { DetectionConfig } from '@/components/cameras/detection/DetectionConfig'
import { IngestionTypeBadge } from '@/components/cameras/IngestionTypeBadge'
import { CameraAlertsSection } from '@/components/cameras/CameraAlertsSection'
import { MyPhoneAlertsSection } from '@/components/cameras/MyPhoneAlertsSection'
import { CameraCluesBetaSwitch } from '@/components/cameras/CameraCluesBetaSwitch'
import { DirectionFields } from '@/components/maps/DirectionFields'
import { directionForm, parseDirection, type DirectionForm, type DirectionValues } from '@/lib/mapDirection'
import { useMapSettings } from '@/hooks/useMapSettings'
import { wedgeColour, wedgeDefaults } from '@/lib/mapSettings'

// CP 236 — same order and labels as the Events page tabs.
const DEFAULT_EVENT_VIEWS: DefaultEventCategory[] = ['animal', 'human', 'other', 'all']

// #330 — the camera's stored direction as the sheet's text fields (null shows as empty)
const cameraDirection = (camera: Camera) =>
  directionForm({ heading: camera.heading_degrees, fov: camera.fov_degrees, range: camera.view_range_m })

/** #330 fix round 2 — the direction fields with the compass in the configured wedge colour, from the cached map
 *  config (loaded by any map this session; red without one). useMapSettings registers no query of its own (#287).
 *  #339 — and the wedge defaults an empty width or range follows (the placeholders; 40 / 10 without a config). */
function ConfiguredDirectionFields(props: { value: DirectionForm; onChange: (next: DirectionForm) => void; disabled: boolean }) {
  const settings = useMapSettings()
  return <DirectionFields {...props} colour={wedgeColour(settings)} defaults={wedgeDefaults(settings)} />
}

interface CameraEditSheetProps {
  camera: Camera
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CameraEditSheet({ camera, open, onOpenChange }: CameraEditSheetProps) {
  const { t } = useTranslation('cameras')
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('events')
  const queryClient = useQueryClient()
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser)  // #074 — audio settings are superuser-only
  // CP 236 — same owner-or-superuser gate as Detection/Alerts below; the server
  // refuses a shared user's camera update anyway.
  const canSetDefaultView = !!isSuperuser || camera.is_owner
  // #410 — owner or superuser edit the fields and Save; a person the camera is shared with sees them read-only. The
  // settings gears and My phone alerts stay for everyone (what a settings tick allows is enforced inside the gears).
  const canEditFields = hasFullRights(camera, isSuperuser)
  const hasSettingsTick = !canEditFields && canChangeCameraSettings(camera, isSuperuser)

  const isG100 = camera.ingestion_type === 'mqtt'   // CP 356

  // Form state — initialised from camera prop
  const [name, setName] = useState(camera.name)
  const [iccid, setIccid] = useState(camera.iccid ?? '')
  const [location, setLocation] = useState(camera.location ?? '')
  const [latitude, setLatitude] = useState(camera.latitude ?? '')
  const [longitude, setLongitude] = useState(camera.longitude ?? '')
  const [isActive, setIsActive] = useState(camera.is_active)
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(camera.group_id)
  const [ingestionType, setIngestionType] = useState<'ftp' | 'http'>(camera.ingestion_type === 'http' ? 'http' : 'ftp')
  const [pendingIngestionType, setPendingIngestionType] = useState<'ftp' | 'http' | null>(null)
  const [httpSettingsOpen, setHttpSettingsOpen] = useState(false)
  const [g100SettingsOpen, setG100SettingsOpen] = useState(false)   // #403
  const [g100DeviceOpen, setG100DeviceOpen] = useState(false)   // #411
  const [defaultView, setDefaultView] = useState<DefaultEventCategory>(camera.default_event_category ?? 'animal')
  // #330 — where the camera looks (superusers only); an empty field saves as null
  const [direction, setDirection] = useState<DirectionForm>(() => cameraDirection(camera))
  // #330 fix round 1 — what the fields were filled with: the three are sent only when one of them differs, so a
  // save that did not touch the section never writes an older direction over one set meanwhile on the map.
  const loadedDirection = useRef<DirectionForm>(direction)
  // #330 round 2 — while a super admin edits the direction here, the fleet map behind the sheet draws it as a
  // preview wedge (store/directionPreview). Only this sheet's own preview is cleared when it closes.
  const setPreview = useDirectionPreview((s) => s.set)
  const clearPreview = useDirectionPreview((s) => s.clear)
  const publishedPreview = useRef(false)
  const endPreview = useCallback(() => {
    if (!publishedPreview.current) return
    publishedPreview.current = false
    clearPreview()
  }, [clearPreview])
  const changeDirection = (next: DirectionForm) => {
    setDirection(next)
    const values = parseDirection(next)
    if (!values) return   // text that is not valid: the last valid preview stays
    if (values.heading_degrees === null) { endPreview(); return }   // no heading, no wedge to preview
    publishedPreview.current = true
    setPreview({ id: camera.id, heading: values.heading_degrees, fov: values.fov_degrees, rangeM: values.view_range_m })
  }
  // Closed (Cancel, the X, Escape, a save, the parent), or gone: the map goes back to the saved wedge
  useEffect(() => { if (!open) endPreview() }, [open, endPreview])
  useEffect(() => endPreview, [endPreview])

  // Reset form when camera changes or sheet opens
  useEffect(() => {
    if (open) {
      setName(camera.name)
      setIccid(camera.iccid ?? '')
      setLocation(camera.location ?? '')
      setLatitude(camera.latitude ?? '')
      setLongitude(camera.longitude ?? '')
      setIsActive(camera.is_active)
      setSelectedGroupId(camera.group_id)
      setIngestionType(camera.ingestion_type === 'http' ? 'http' : 'ftp')
      setPendingIngestionType(null)
      setDefaultView(camera.default_event_category ?? 'animal')
      setDirection(cameraDirection(camera))
      loadedDirection.current = cameraDirection(camera)
      endPreview()   // #330 round 2 — the fields show the stored direction again, and so does the map
    }
  }, [open, camera, endPreview])

  // Fetch camera groups for the group selector
  const { data: cameraGroups = [] } = useQuery<CameraGroup[]>({
    queryKey: queryKeys.cameraGroups.all(),
    queryFn: () => cameraGroupsApi.getAll(),
    ...queryOptions.cameraGroups.all,
    enabled: open,
  })

  // Save mutation (handles both group membership and camera fields)
  const updateMutation = useMutation({
    mutationFn: async (payload: {
      cameraData: Parameters<typeof camerasApi.update>[1]
      newGroupId: number | null
    }) => {
      const { cameraData, newGroupId } = payload
      const previousGroupId = camera.group_id

      const groupOps: Promise<unknown>[] = []

      // #152 — the rule lives in planGroupSaveOps so it can be tested directly:
      // never remove a camera from a group the requester does not own.
      const { remove, add } = planGroupSaveOps(previousGroupId, newGroupId, cameraGroups)

      if (remove) groupOps.push(cameraGroupsApi.removeCameras(remove, [camera.id]))
      if (add) groupOps.push(cameraGroupsApi.addCameras(add, [camera.id]))

      if (groupOps.length) {
        await Promise.all(groupOps)
      }

      return camerasApi.update(camera.id, cameraData)
    },
    onSuccess: () => {
      endPreview()   // #330 round 2 — saved: the refreshed camera draws the solid wedge
      toast.success(t('messages.saved'))
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
      const detail = data?.detail
      const msg = typeof detail === 'string' ? detail : t('messages.saveFailed')
      toast.error(msg)
    },
  })

  const handleSave = () => {
    if (!name.trim()) {
      toast.error(t('messages.nameRequired'))
      return
    }
    // #330 — sent by a superuser only: the API refuses the three fields from anyone else
    const directionValues = isSuperuser ? parseDirection(direction) : null
    if (isSuperuser && !directionValues) {
      toast.error(t('edit.directionInvalid'))
      return
    }
    const loaded = parseDirection(loadedDirection.current)
    const directionChanged = !!directionValues
      && (!loaded || (Object.keys(directionValues) as (keyof DirectionValues)[]).some((k) => directionValues[k] !== loaded[k]))
    updateMutation.mutate({
      cameraData: {
        name: name.trim(),
        iccid: iccid.trim() || undefined,
        location: location.trim() || undefined,
        latitude: latitude.trim() || null,
        longitude: longitude.trim() || null,
        is_active: isActive,
        // CP 356: a G100's connection type is locked; never sent
        ...(!isG100 && { ingestion_type: ingestionType }),
        ...(canSetDefaultView && { default_event_category: defaultView }),
        ...(directionChanged && directionValues),
      },
      newGroupId: selectedGroupId,
    })
  }

  const isSaving = updateMutation.isPending
  const fieldsLocked = isSaving || !canEditFields

  return (
    <>
    <Dialog.Root open={open} onOpenChange={isSaving ? undefined : onOpenChange}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        {/* Slide-in panel from right */}
        <Dialog.Content
          className="fixed right-0 top-0 z-50 h-full w-full max-w-md border-l border-border bg-background shadow-elevation-3 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-300"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <Settings className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <Dialog.Title className="text-base font-semibold truncate">
                  {t('edit.title')}
                </Dialog.Title>
                <p className="text-xs text-muted-foreground truncate">{camera.name}</p>
              </div>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" disabled={isSaving}>
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">

            {!canEditFields && (
              <p role="note" data-testid="share-readonly-note" className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                {hasSettingsTick ? t('edit.ownerOnlySettings') : t('edit.viewOnlyNote')}
              </p>
            )}

            {/* Identity */}
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t('edit.sectionIdentity')}
              </h3>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-name">{t('edit.name')}</label>
                <Input
                  id="cam-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('edit.namePlaceholder')}
                  disabled={fieldsLocked}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-location">{t('edit.location')}</label>
                <Input
                  id="cam-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t('edit.locationPlaceholder')}
                  disabled={fieldsLocked}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('edit.coordinates')}</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    placeholder={t('edit.latPlaceholder')}
                    disabled={fieldsLocked}
                  />
                  <Input
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    placeholder={t('edit.lngPlaceholder')}
                    disabled={fieldsLocked}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t('edit.coordinatesHint')}
                </p>
                {canEditFields && <LookupAddressButton
                  lat={latitude}
                  lng={longitude}
                  onAddress={(addr) => setLocation(addr)}
                  className="mt-2"
                  buttonLabel={t('edit.lookupAddress')}
                />}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-group">{t('edit.group')}</label>
                <Select
                  value={selectedGroupId === null ? 'none' : String(selectedGroupId)}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedGroupId(null)
                    } else {
                      setSelectedGroupId(Number(value))
                    }
                  }}
                  disabled={fieldsLocked || cameraGroups.length === 0}
                >
                  <SelectTrigger id="cam-group">
                    <SelectValue placeholder={cameraGroups.length ? t('edit.selectGroup') : t('edit.noGroups')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('edit.noGroupOption')}</SelectItem>
                    {cameraGroups.map((group) => (
                      <SelectItem key={group.id} value={String(group.id)}>
                        {/* #152 — names are unique per USER, so two owners can both
                            have a "JR East". Qualify the ones we don't own. */}
                        {group.is_owner === false
                          ? `${group.name} — ${group.owner_email ?? t('edit.sharedGroupSuffix')}`
                          : group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-iccid">{t('edit.iccid')}</label>
                <Input
                  id="cam-iccid"
                  value={iccid}
                  onChange={(e) => setIccid(e.target.value)}
                  placeholder={t('edit.iccidPlaceholder')}
                  className="font-mono text-sm"
                  disabled={fieldsLocked}
                />
                <p className="text-xs text-muted-foreground">{t('edit.iccidHint')}</p>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cam-ingestion-type">{t('edit.ingestionType')}</label>
                {isG100 ? (
                  <div data-testid="ingestion-type-readonly" className="flex items-center gap-2 text-sm">
                    <IngestionTypeBadge type="mqtt" />
                  </div>
                ) : (<>
                <Select
                  value={ingestionType}
                  onValueChange={(value) => {
                    const next = value as 'ftp' | 'http'
                    if (next !== ingestionType) {
                      setPendingIngestionType(next)
                    }
                  }}
                  disabled={fieldsLocked}
                >
                  <SelectTrigger id="cam-ingestion-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ftp">{t('edit.ingestionFtp')}</SelectItem>
                    <SelectItem value="http">{t('edit.ingestionHttp')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t('edit.ingestionTypeHint')}</p>
                </>)}
              </div>

              {/* HTTP camera config push (Change #032) — only for cameras that
                  are PERSISTED as ingestion_type=http on the server.
                  We deliberately use camera.ingestion_type, not the local
                  ingestionType form state, so the button only shows once
                  the protocol switch has been saved. Otherwise the user
                  could draft a switch + save desired_* against an FTP
                  camera that will never POST /api/image to receive them. */}
              {camera.ingestion_type === 'http' && (
                <div className="space-y-1.5">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setHttpSettingsOpen(true)}
                    disabled={isSaving}
                  >
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    HTTP camera settings
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Push camera shooting parameters, schedule, storage, and one-shot commands to the camera firmware over HTTP.
                  </p>
                </div>
              )}

              {/* #403 — a G100 camera's settings (capture, triggering, deterrence, AI, power), through the owner API. */}
              {isG100 && (
                <div className="space-y-1.5">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setG100SettingsOpen(true)}
                    disabled={isSaving}
                  >
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    {t('edit.g100Settings')}
                  </Button>
                  <p className="text-xs text-muted-foreground">{t('edit.g100SettingsHint')}</p>
                </div>
              )}

              {/* #411 — a G100 camera's status report and its six owner actions, through the owner API. Same rule as the
                  settings button above; the sheet itself shows the actions only to a user the server lets control it. */}
              {isG100 && (
                <div className="space-y-1.5">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setG100DeviceOpen(true)}
                    disabled={isSaving}
                  >
                    <Activity className="h-4 w-4 mr-2" />
                    {t('g100Device.button')}
                  </Button>
                  <p className="text-xs text-muted-foreground">{t('g100Device.buttonHint')}</p>
                </div>
              )}

              {/* Read-only IMEI */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-muted-foreground">{t('edit.imei')}</label>
                <div className="rounded-md bg-muted/50 px-3 py-2 font-mono text-sm text-muted-foreground select-all">
                  {camera.imei}
                </div>
              </div>
            </section>

            {/* #330 — where the camera looks, drawn as a wedge on the fleet map; superusers only (the phone's way
                to set it; on a computer it is also set from the pin) */}
            {isSuperuser && (
              <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {t('edit.direction')}
                </h3>
                <ConfiguredDirectionFields value={direction} onChange={changeDirection} disabled={isSaving} />
              </section>
            )}

            {/* Detection — owners tune per-class confidence on their own cameras (Phase 2);
                superusers get the full controls. */}
            {(isSuperuser || camera.is_owner) && (
              <DetectionConfig cameraId={camera.id} open={open} isSuperuser={!!isSuperuser} />
            )}

            {/* CP 159 — who gets told. Same owner-or-superuser gate as Detection above,
                matching the server rule: a shared user gets 403 from push-settings. */}
            {(isSuperuser || camera.is_owner) && (
              <CameraAlertsSection camera={camera} isSuperuser={!!isSuperuser} />
            )}

            {/* CP 391 — My phone alerts: the viewer's OWN push choice for this camera, for EVERY viewer (shared
                users included). It saves on its own (its own endpoint), not through the Save button below. */}
            <MyPhoneAlertsSection camera={camera} open={open} />

            {/* #408 — the camera's Beta switch; superusers only (the server refuses everyone else). It saves on its own. */}
            {isSuperuser && <CameraCluesBetaSwitch key={`clues-beta-${camera.id}`} camera={camera} />}

            {/* Camera Toggles */}
            <section className="space-y-1">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                {t('edit.sectionSettings')}
              </h3>
              {/* CP 189 — superuser-only, HTTP-only, saves on toggle rather than
                  on this sheet's Save button (it writes its own endpoint). */}
              <OnlineStatusModeRow camera={camera} isSuperuser={!!isSuperuser} />
              {canSetDefaultView && (
                <div className="space-y-1.5 py-2">
                  <label className="text-sm font-medium" htmlFor="cam-default-view">{t('edit.defaultEventsView')}</label>
                  <Select
                    value={defaultView}
                    onValueChange={(value) => setDefaultView(value as DefaultEventCategory)}
                    disabled={isSaving}
                  >
                    <SelectTrigger id="cam-default-view">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEFAULT_EVENT_VIEWS.map((c) => (
                        <SelectItem key={c} value={c}>{te(`tabs.${c}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{t('edit.defaultEventsViewHint')}</p>
                </div>
              )}
              <SwitchRow
                id="sw-active"
                label={t('edit.cameraActive')}
                description={t('edit.activeHint')}
                checked={isActive}
                onCheckedChange={setIsActive}
                disabled={fieldsLocked}
              />
            </section>

          </div>

          {/* Footer */}
          <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => { endPreview(); onOpenChange(false) }}
              disabled={isSaving}
            >
              {canEditFields ? tc('actions.cancel') : tc('actions.close')}
            </Button>
            {canEditFields && (
              <Button
                className="flex-1"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSaving ? t('edit.saving') : t('edit.saveChanges')}
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>

    <ConfirmDialog
      open={pendingIngestionType !== null}
      onOpenChange={(o) => { if (!o) setPendingIngestionType(null) }}
      title={t('edit.ingestionConfirmTitle')}
      description={t('edit.ingestionConfirmBody', {
        newProtocol: pendingIngestionType === 'http' ? t('edit.ingestionHttp') : t('edit.ingestionFtp'),
      })}
      confirmLabel={t('edit.ingestionConfirmAction')}
      variant="destructive"
      onConfirm={() => {
        if (pendingIngestionType) setIngestionType(pendingIngestionType)
        setPendingIngestionType(null)
      }}
    />

    {/* HTTP camera settings sub-sheet (Change #032). Independent of the
        edit sheet save flow — the new sheet PATCHes the camera directly. */}
    <CameraHttpSettingsSheet
      camera={camera}
      open={httpSettingsOpen}
      onOpenChange={setHttpSettingsOpen}
    />

    {/* #403 — G100 camera settings sub-sheet, through the owner API. */}
    {isG100 && (
      <CameraG100SettingsSheet
        camera={camera}
        open={g100SettingsOpen}
        onOpenChange={setG100SettingsOpen}
      />
    )}

    {/* #411 — G100 status and actions sub-sheet, through the owner API. */}
    {isG100 && (
      <CameraG100DeviceSheet
        camera={camera}
        open={g100DeviceOpen}
        onOpenChange={setG100DeviceOpen}
      />
    )}
    </>
  )
}
